import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { italianProvinces } from '../../data/italianProvinces';

interface PersonalDetailsFormProps {
  onComplete: (details: PersonalDetails) => void;
  initialData?: PersonalDetails | null;
  isRegistrationFlow?: boolean;
}

interface PersonalDetails {
  record_id?: string;
  user_id?: string;
  nome: string;
  cognome: string;
  data_nascita?: string | null;
  luogo_nascita?: string;
  provincia_nascita?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  cap?: number | null;
  luogo_residenza?: string;
  provincia_residenza?: string;
  telefono?: string;
  attestato_nr?: string;
  tipo?: string;
  scadenza_visita_medica?: string | null;
  attestato_file_url?: string;
  attestato_file?: File | null;
  open_notes?: string;
  preferred_language?: string;
}

// Convert string to CamelCase (Title Case)
const toCamelCase = (str: string): string => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PersonalDetailsForm: React.FC<PersonalDetailsFormProps> = ({
  onComplete,
  initialData,
  isRegistrationFlow = false
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PersonalDetails>({
    nome: '',
    cognome: '',
    data_nascita: null,
    luogo_nascita: '',
    provincia_nascita: '',
    codice_fiscale: '',
    indirizzo: '',
    cap: null,
    luogo_residenza: '',
    provincia_residenza: '',
    telefono: '',
    attestato_nr: '',
    tipo: 'Monoposto',
    scadenza_visita_medica: null,
    attestato_file: null,
    open_notes: '',
    preferred_language: i18n.language?.substring(0, 2) === 'it' ? 'it' : 'en'
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData
      });
    } else if (isRegistrationFlow) {
      // Load existing personal details if available
      loadPersonalDetails();
    }
  }, [initialData, isRegistrationFlow]);

  const loadPersonalDetails = async () => {
    try {
      const response = await api.get('/personal-details');
      if (response.data) {
        setFormData(response.data);
      }
    } catch (err) {
      // No existing personal details, that's ok
    }
  };

  const handleChange = (field: keyof PersonalDetails) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: string | boolean = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    // Apply CamelCase transformation for specific fields
    const camelCaseFields: (keyof PersonalDetails)[] = [
      'nome', 'cognome', 'luogo_nascita', 'indirizzo', 'luogo_residenza'
    ];
    if (camelCaseFields.includes(field) && typeof value === 'string') {
      value = toCamelCase(value);
    }

    // Apply uppercase transformation for codice_fiscale
    if (field === 'codice_fiscale' && typeof value === 'string') {
      value = value.toUpperCase();
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field: 'data_nascita' | 'scadenza_visita_medica') => (
    value: any
  ) => {
    // The value can be a Dayjs, Date, or null depending on the adapter
    if (value && typeof value === 'object') {
      try {
        let dateValue: string;
        if (value.toISOString && typeof value.toISOString === 'function') {
          // For Dayjs or Date objects with toISOString method
          dateValue = value.toISOString();
        } else if (value.format && typeof value.format === 'function') {
          // For Dayjs objects, use ISO format
          dateValue = value.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        } else {
          // Fallback to string conversion
          dateValue = value.toString();
        }
        setFormData(prev => ({
          ...prev,
          [field]: dateValue
        }));
      } catch (error) {
        console.error('Date conversion error:', error);
        // Set to null if conversion fails
        setFormData(prev => ({
          ...prev,
          [field]: null
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, attestato_file: e.target.files![0] }));
    }
  };

  const handleSelectChange = (field: keyof PersonalDetails) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'provincia_nascita' && value === 'Estero') {
      setFormData(prev => ({ ...prev, luogo_nascita: 'Estero' }));
    } else if (field === 'provincia_residenza' && value === 'Estero') {
      setFormData(prev => ({ ...prev, luogo_residenza: 'Estero' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.nome || !formData.cognome ||
          !formData.provincia_nascita || !formData.luogo_nascita ||
          !formData.codice_fiscale || !formData.telefono ||
          !formData.indirizzo || !formData.provincia_residenza || !formData.luogo_residenza) {
        throw new Error(t('personalDetails.errors.requiredFields'));
      }


      let response;
      if (formData.record_id) {
        // Update existing - exclude user_id from update payload
        const { user_id, attestato_file, ...updateData } = formData;
        
        // If there's a file to upload, use FormData
        if (attestato_file) {
          const formDataObj = new FormData();
          formDataObj.append('personal_details_data', JSON.stringify(updateData));
          formDataObj.append('attestato_file', attestato_file);
          
          response = await api.put('/personal-details-with-file', formDataObj, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          response = await api.put('/personal-details', updateData);
        }
      } else {
        // Create new
        const { attestato_file, ...dataWithoutFile } = formData;
        const dataToSend = {
          ...dataWithoutFile,
          privacy_consent: true  // User already accepted privacy in registration step 1
        };
        
        // If there's a file to upload, use FormData
        if (attestato_file) {
          const formDataObj = new FormData();
          formDataObj.append('personal_details_data', JSON.stringify(dataToSend));
          formDataObj.append('attestato_file', attestato_file);
          
          response = await api.post('/personal-details-with-file', formDataObj, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          response = await api.post('/personal-details', dataToSend);
        }
      }

      onComplete(response.data);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = t('personalDetails.errors.saveFailed');
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage = errorDetail[0].msg || errorDetail[0].message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const gliderTypes = [
    { value: 'Monoposto', label: t('personalDetails.gliderTypes.monoposto') },
    { value: 'Biposto', label: t('personalDetails.gliderTypes.biposto') },
    { value: 'PPG', label: t('personalDetails.gliderTypes.ppg') }
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>
          {t('personalDetails.title')}
        </Typography>
        
        {isRegistrationFlow && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('personalDetails.registrationInfo')}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Basic Information */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              required
              label={t('personalDetails.firstName')}
              value={formData.nome}
              onChange={handleChange('nome')}
              disabled={loading}
            />
            
            <TextField
              fullWidth
              required
              label={t('personalDetails.lastName')}
              value={formData.cognome}
              onChange={handleChange('cognome')}
              disabled={loading}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t('personalDetails.birthDate')}
                value={formData.data_nascita ? dayjs(formData.data_nascita) : null}
                onChange={handleDateChange('data_nascita')}
                disabled={loading}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth required>
              <InputLabel>{t('personalDetails.birthProvince')}</InputLabel>
              <Select
                value={formData.provincia_nascita || ''}
                onChange={handleSelectChange('provincia_nascita')}
                label={t('personalDetails.birthProvince')}
                disabled={loading}
                required
              >
                <MenuItem value="">{t('registration.personalInfo.selectProvince')}</MenuItem>
                <MenuItem value="Estero">EE - ABROAD/ESTERO</MenuItem>
                {italianProvinces.map((prov) => (
                  <MenuItem key={prov.provincia} value={prov.provincia}>
                    {prov.sigla} - {prov.provincia}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              label={t('personalDetails.birthPlace')}
              value={formData.luogo_nascita || ''}
              onChange={handleChange('luogo_nascita')}
              disabled={loading || !formData.provincia_nascita || formData.provincia_nascita === 'Estero'}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              required
              label={t('personalDetails.taxCode')}
              value={formData.codice_fiscale || ''}
              onChange={handleChange('codice_fiscale')}
              disabled={loading}
              inputProps={{ maxLength: 16, style: { textTransform: 'uppercase' } }}
            />
            <Box sx={{ flex: 1 }} /> {/* Spacer for alignment */}
          </Box>

          {/* Address Information */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t('personalDetails.addressSection')}
          </Typography>

          <TextField
            fullWidth
            required
            label={t('personalDetails.address')}
            value={formData.indirizzo || ''}
            onChange={handleChange('indirizzo')}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormControl fullWidth sx={{ flex: 1 }} required>
              <InputLabel>{t('personalDetails.province')}</InputLabel>
              <Select
                value={formData.provincia_residenza || ''}
                onChange={handleSelectChange('provincia_residenza')}
                label={t('personalDetails.province')}
                disabled={loading}
                required
              >
                <MenuItem value="">{t('registration.personalInfo.selectProvince')}</MenuItem>
                <MenuItem value="Estero">EE - ABROAD/ESTERO</MenuItem>
                {italianProvinces.map((prov) => (
                  <MenuItem key={prov.provincia} value={prov.provincia}>
                    {prov.sigla} - {prov.provincia}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              label={t('personalDetails.city')}
              value={formData.luogo_residenza || ''}
              onChange={handleChange('luogo_residenza')}
              disabled={loading || !formData.provincia_residenza || formData.provincia_residenza === 'Estero'}
              sx={{ flex: 2 }}
            />

            <TextField
              fullWidth
              label={t('personalDetails.postalCode')}
              value={formData.cap || ''}
              onChange={handleChange('cap')}
              disabled={loading}
              type="number"
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Contact Information */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t('personalDetails.contactSection')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              required
              label={t('personalDetails.phone')}
              value={formData.telefono || ''}
              onChange={handleChange('telefono')}
              disabled={loading}
              type="tel"
              placeholder="+39 123 456 7890"
              helperText={t('personalDetails.phoneHelp')}
              inputProps={{
                pattern: "\\+[1-9][\\d\\s]{1,17}",
                title: "Please enter phone number in international format (+country code followed by number)"
              }}
            />

            <TextField
              select
              fullWidth
              label={t('personalDetails.preferredLanguage')}
              value={formData.preferred_language || 'it'}
              onChange={handleChange('preferred_language')}
              disabled={loading}
            >
              <MenuItem value="it">Italiano</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </TextField>
          </Box>

          {/* Flight Information */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t('personalDetails.flightSection')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              label={t('personalDetails.licenseNumber')}
              value={formData.attestato_nr || ''}
              onChange={handleChange('attestato_nr')}
              disabled={loading}
              autoComplete="off"
            />

            <TextField
              select
              fullWidth
              label={t('personalDetails.licenseType', 'Abilitazione')}
              value={formData.tipo || 'Monoposto'}
              onChange={handleChange('tipo')}
              disabled={loading}
            >
              {gliderTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUpload />}
              sx={{ height: '56px' }}
              disabled={loading}
            >
              {formData.attestato_file ? formData.attestato_file.name : t('registration.fields.uploadLicense')}
              <input
                hidden
                accept=".jpg,.jpeg,.png,.pdf"
                type="file"
                onChange={handleFileChange}
              />
            </Button>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t('personalDetails.medicalExpiry')}
                value={formData.scadenza_visita_medica ? dayjs(formData.scadenza_visita_medica) : null}
                onChange={handleDateChange('scadenza_visita_medica')}
                disabled={loading}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('registration.fields.notes')}
            value={formData.open_notes || ''}
            onChange={handleChange('open_notes')}
            disabled={loading}
            placeholder="Additional notes or qualifications..."
          />

          {/* Submit Button */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t(isRegistrationFlow ? 'personalDetails.continue' : 'personalDetails.save')
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default PersonalDetailsForm;